const persianNumberToEnglishNumber: Record<string, string> = {
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
};
const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

export const removeNonNumbers = (text: string) => text.replace(/[^0-9.]/g, '');
type InputNumber = string | number | null | undefined;

/**
 * @function convertPersianNumberToEnglishNumber
 * @description
 * # convertPersianNumberToEnglishNumber
 *
 * @param  {InputNumber} persianInput
 * @returns {string} an string that has every persian number replaced with respective english ones
 *
 * @example convertPersianNumberToEnglishNumber('۱۲three4پنج'); // '12three4پنج'
 * @note Read tests for more examples
 * */
export const convertPersianNumberToEnglishNumber = (persianInput: InputNumber): string => {
  if (!persianInput && persianInput !== 0) return '';
  let resultStr = String(persianInput);
  for (const persianNumber in persianNumberToEnglishNumber) {
    resultStr = resultStr.replace(RegExp(persianNumber, 'g'), persianNumberToEnglishNumber[persianNumber]);
  }
  return resultStr;
};

/**
 * @function convertEnglishNumberToPersianNumber
 * @description
 * # convertEnglishNumberToPersianNumber
 *
 * @param  {InputNumber} englishInput
 * @returns {string} an string that converts every english number to persian ones
 *
 * @example convertEnglishNumberToPersianNumber('۱2three4پنج'); // '۱۲three۴پنج'
 * @note Read tests for more examples
 * */
export const convertEnglishNumberToPersianNumber = (englishInput: InputNumber): string => {
  if (!englishInput && englishInput !== 0) return '';
  let resultStr = String(englishInput);
  for (let i = 0; i < 10; i += 1) {
    resultStr = resultStr.replace(RegExp(i.toString(), 'g'), persianNumbers[i]);
  }
  return resultStr;
};

const PERSIAN_ORDER_STRING = ['صفرم', 'اول', 'دوم', 'سوم', 'چهارم', 'پنجم', 'ششم', 'هفتم', 'هشتم', 'نهم', 'دهم'];
/**
 * @function appendPersianOrderToString
 * @description
 * # appendPersianOrderToString
 *
 * @param  {number} order   A number indicating the order of things
 * @param  {string} baseString    The base string that the persian order will be appended to - Default to ''
 * @param  {number} startCountFrom    If the order is larger than this number, it will be converted to the persian order - Default to 0
 * @returns {string}
 *
 * @example appendPersianOrderToString(3, 'مقصد' ); // مقصد سوم
 * @note Read tests for more examples
 * */
export const appendPersianOrderToString = (order: number, baseString: string = '', startCountFrom: number = 0): string => {
  const isOrderValid = order || order === 0;
  const persianOrder = PERSIAN_ORDER_STRING[Number(order)] ?? '';
  const canConvert = isOrderValid && Number(order) >= startCountFrom && persianOrder;
  const delimiter = baseString ? ' ' : '';
  return canConvert ? `${baseString}${delimiter}${persianOrder}` : baseString;
};
// core duplicate
/**
 * @function separateEvery3digit
 * @description
 * # separateEvery3digit
 *
 * @param  {InputNumber} value   Input number (in number or string format)
 * @param  {string} delimiter    The delimiter between each 3 digits - Default to ','
 * @returns {string}
 *
 * @example separateEvery3digit(300000); // 300,000
 * @note Read tests for more examples
 * */
export function separateEvery3digit(value: InputNumber, delimiter?: string): string {
  if (value || value === 0) return String(value).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${delimiter || ','}`);
  return '';
}

export const formatSecondToMinutes = (second: number): string => {
  const minutes = Math.floor(second / 60);
  const seconds = second % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const trimPriceThousandZero = (value: InputNumber) => {
  if (!value) return;
  return Number(value) / 1000;
};
